// Google Drive API Service for EasyShopi
// Handles authentication, uploads, metadata, and image management

class GoogleDriveService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    this.scope = 'https://www.googleapis.com/auth/drive.file';
    this.discoveryDocs = ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'];
    this.accessToken = null;
    this.refreshToken = null;
    this.folderId = null;
    this.isAuthenticated = false;
    this.isDemoMode = false;
    this.rateLimiter = {
      requests: 0,
      resetTime: Date.now() + 60000, // Reset every minute
      maxRequests: 100 // Google Drive API rate limit
    };

    // Check if we're in demo mode (no credentials)
    this.checkDemoMode();
  }

  // Check if we should use demo mode
  checkDemoMode() {
    if (!this.apiKey || !this.clientId ||
        this.apiKey === 'your_google_api_key_here' ||
        this.clientId === 'your_google_oauth_client_id_here') {
      this.isDemoMode = true;
      console.warn('Google Drive is in demo mode. Configure credentials to enable full functionality.');
    }
  }

  // Initialize Google API
  async init() {
    return new Promise((resolve, reject) => {
      // Check if credentials are configured
      if (!this.apiKey || !this.clientId ||
          this.apiKey === 'your_google_api_key_here' ||
          this.clientId === 'your_google_oauth_client_id_here') {
        reject(new Error('Google Drive credentials not configured. Please run: node setup-google-drive.js'));
        return;
      }

      if (typeof window.gapi === 'undefined') {
        // Load Google API script
        const script = document.createElement('script');
        script.src = 'https://apis.google.com/js/api.js';
        script.onload = () => this.loadGapi().then(resolve).catch(reject);
        script.onerror = () => reject(new Error('Failed to load Google API. Check your internet connection.'));
        document.head.appendChild(script);
      } else {
        this.loadGapi().then(resolve).catch(reject);
      }
    });
  }

  // Load Google API client
  async loadGapi() {
    return new Promise((resolve, reject) => {
      window.gapi.load('auth2', () => {
        window.gapi.auth2.init({
          api_key: this.apiKey,
          client_id: this.clientId,
          discovery_docs: this.discoveryDocs,
          scope: this.scope
        }).then(() => {
          console.log('Google API initialized');
          resolve();
        }).catch((error) => {
          console.error('Google API init failed:', error);
          reject(new Error('Google API initialization failed. Check your credentials and authorized origins.'));
        });
      });
    });
  }

  // Authenticate user with Google Drive
  async authenticate() {
    try {
      if (this.isDemoMode) {
        throw new Error('Demo mode: Google Drive credentials not configured. Please run: node setup-google-drive.js');
      }

      const authInstance = window.gapi.auth2.getAuthInstance();
       
      if (!authInstance.isSignedIn.get()) {
        const user = await authInstance.signIn();
        this.isAuthenticated = true;
      }

      const authResponse = authInstance.currentUser.get().getAuthResponse();
      this.accessToken = authResponse.access_token;
      this.refreshToken = authResponse.refresh_token;

      // Store tokens securely
      this.storeTokens();

      console.log('Google Drive authentication successful');
      return true;
    } catch (error) {
      console.error('Google Drive authentication failed:', error);
      
      if (this.isDemoMode) {
        throw new Error('Demo mode active. Configure Google Drive credentials to enable authentication.');
      }
      
      throw new Error('Authentication failed: ' + error.message);
    }
  }

  // Demo mode upload (returns placeholder)
  async demoUpload(file, metadata = {}) {
    // Simulate upload delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Return placeholder data
    return {
      id: `demo_${Date.now()}`,
      name: `demo_${file.name}`,
      url: `https://via.placeholder.com/400x400?text=Demo+${encodeURIComponent(file.name)}`,
      thumbnailUrl: `https://via.placeholder.com/200x200?text=Demo`,
      size: file.size,
      createdTime: new Date().toISOString(),
      properties: {
        easyshopi_product_id: metadata.productId || '',
        easyshopi_title: metadata.title || '',
        easyshopi_category: metadata.category || '',
        easyshopi_upload_time: new Date().toISOString(),
        easyshopi_demo_mode: 'true'
      }
    };
  }

  // Sign out from Google Drive
  async signOut() {
    try {
      const authInstance = window.gapi.auth2.getAuthInstance();
      await authInstance.signOut();
      
      this.accessToken = null;
      this.refreshToken = null;
      this.isAuthenticated = false;
      this.clearTokens();
      
      console.log('Signed out from Google Drive');
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  }

  // Check if user is authenticated
  isAuth() {
    return this.isAuthenticated && this.accessToken;
  }

  // Create or get EasyShopi folder
  async getOrCreateFolder() {
    if (this.folderId) return this.folderId;

    try {
      // Check if folder already exists
      const existingFolder = await this.searchFolder('EasyShopi-Images');
      if (existingFolder.length > 0) {
        this.folderId = existingFolder[0].id;
        return this.folderId;
      }

      // Create new folder
      const folderMetadata = {
        name: 'EasyShopi-Images',
        mimeType: 'application/vnd.google-apps.folder'
      };

      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(folderMetadata)], { type: 'application/json' }));

      const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        },
        body: form
      });

      if (!response.ok) {
        throw new Error(`Failed to create folder: ${response.statusText}`);
      }

      const folder = await response.json();
      this.folderId = folder.id;
      
      console.log('Created EasyShopi folder:', this.folderId);
      return this.folderId;
    } catch (error) {
      console.error('Error creating/getting folder:', error);
      throw error;
    }
  }

  // Search for folder by name
  async searchFolder(folderName) {
    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files?q='me' in owners and name='${folderName}' and mimeType='application/vnd.google-apps.folder'&fields=files(id,name,createdTime,modifiedTime)`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data.files || [];
    } catch (error) {
      console.error('Error searching folder:', error);
      return [];
    }
  }

  // Upload image to Google Drive
  async uploadImage(file, metadata = {}) {
    // Handle demo mode
    if (this.isDemoMode || !this.isAuth()) {
      if (this.isDemoMode) {
        console.log('Demo mode: Simulating image upload');
        return this.demoUpload(file, metadata);
      } else {
        throw new Error('Not authenticated with Google Drive');
      }
    }

    await this.checkRateLimit();

    try {
      const folderId = await this.getOrCreateFolder();
      
      // Generate unique filename
      const timestamp = Date.now();
      const fileName = `${timestamp}-${file.name}`;
      
      // Prepare metadata
      const fileMetadata = {
        name: fileName,
        parents: [folderId],
        properties: {
          easyshopi_product_id: metadata.productId || '',
          easyshopi_title: metadata.title || '',
          easyshopi_category: metadata.category || '',
          easyshopi_upload_time: new Date().toISOString(),
          easyshopi_original_name: file.name,
          easyshopi_file_size: file.size.toString()
        }
      };

      // Create multipart upload
      const boundary = '-------314159265358979323846';
      const delimiter = `\r\n--${boundary}\r\n`;
      const closeDelim = `\r\n--${boundary}--`;

      const body = [
        delimiter,
        'Content-Type: application/json; charset=UTF-8\r\n',
        '\r\n',
        JSON.stringify(fileMetadata),
        delimiter,
        `Content-Type: ${file.type}\r\n`,
        '\r\n',
        '',
        ''
      ].join('');

      const fileContent = await this.fileToArrayBuffer(file);
      const uint8Array = new Uint8Array(fileContent);
      
      const bodyWithFile = [
        body,
        uint8Array,
        closeDelim
      ];

      const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink,thumbnailLink,size,createdTime,modifiedTime,properties', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': `multipart/related; boundary=${boundary}`
        },
        body: this.concatenateArrayBuffers(bodyWithFile)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }

      const uploadedFile = await response.json();
      
      // Make file publicly viewable
      await this.makeFilePublic(uploadedFile.id);

      console.log('Image uploaded successfully:', uploadedFile.id);
      return {
        id: uploadedFile.id,
        name: uploadedFile.name,
        url: `https://drive.google.com/uc?export=view&id=${uploadedFile.id}`,
        thumbnailUrl: uploadedFile.thumbnailLink,
        size: uploadedFile.size,
        createdTime: uploadedFile.createdTime,
        properties: uploadedFile.properties
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }
  }

  // Make file publicly viewable
  async makeFilePublic(fileId) {
    try {
      const permission = {
        role: 'reader',
        type: 'anyone'
      };

      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}/permissions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(permission)
      });

      if (!response.ok) {
        console.warn('Failed to make file public:', response.statusText);
      }
    } catch (error) {
      console.warn('Error making file public:', error);
    }
  }

  // Get images from EasyShopi folder
  async getImages(pageToken = null, pageSize = 20) {
    if (!this.isAuth()) {
      throw new Error('Not authenticated with Google Drive');
    }

    await this.checkRateLimit();

    try {
      const folderId = await this.getOrCreateFolder();
      
      let query = `'${folderId}' in parents and mimeType contains 'image/' and trashed = false`;
      
      const url = new URL('https://www.googleapis.com/drive/v3/files');
      url.searchParams.set('q', query);
      url.searchParams.set('fields', 'nextPageToken,files(id,name,webViewLink,thumbnailLink,size,createdTime,modifiedTime,properties,parents)');
      url.searchParams.set('pageSize', pageSize.toString());
      url.searchParams.set('orderBy', 'modifiedTime desc');
      
      if (pageToken) {
        url.searchParams.set('pageToken', pageToken);
      }

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch images: ${response.statusText}`);
      }

      const data = await response.json();
      
      const images = (data.files || []).map(file => ({
        id: file.id,
        name: file.name,
        url: `https://drive.google.com/uc?export=view&id=${file.id}`,
        thumbnailUrl: file.thumbnailLink,
        size: parseInt(file.size || '0'),
        createdTime: file.createdTime,
        modifiedTime: file.modifiedTime,
        properties: file.properties || {},
        isEasyshopiImage: file.properties && file.properties.easyshopi_product_id
      }));

      return {
        images,
        nextPageToken: data.nextPageToken
      };
    } catch (error) {
      console.error('Error fetching images:', error);
      throw error;
    }
  }

  // Delete image from Google Drive
  async deleteImage(fileId) {
    if (!this.isAuth()) {
      throw new Error('Not authenticated with Google Drive');
    }

    await this.checkRateLimit();

    try {
      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      });

      if (!response.ok && response.status !== 404) {
        throw new Error(`Failed to delete image: ${response.statusText}`);
      }

      console.log('Image deleted successfully:', fileId);
      return true;
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }

  // Update image metadata
  async updateImageMetadata(fileId, metadata) {
    if (!this.isAuth()) {
      throw new Error('Not authenticated with Google Drive');
    }

    await this.checkRateLimit();

    try {
      const fileMetadata = {
        properties: metadata
      };

      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(fileMetadata)
      });

      if (!response.ok) {
        throw new Error(`Failed to update metadata: ${response.statusText}`);
      }

      const updatedFile = await response.json();
      console.log('Metadata updated successfully:', fileId);
      return updatedFile;
    } catch (error) {
      console.error('Error updating metadata:', error);
      throw error;
    }
  }

  // Get image metadata
  async getImageMetadata(fileId) {
    if (!this.isAuth()) {
      throw new Error('Not authenticated with Google Drive');
    }

    await this.checkRateLimit();

    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?fields=id,name,webViewLink,thumbnailLink,size,createdTime,modifiedTime,properties,parents`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get metadata: ${response.statusText}`);
      }

      const file = await response.json();
      return {
        id: file.id,
        name: file.name,
        url: `https://drive.google.com/uc?export=view&id=${file.id}`,
        thumbnailUrl: file.thumbnailLink,
        size: parseInt(file.size || '0'),
        createdTime: file.createdTime,
        modifiedTime: file.modifiedTime,
        properties: file.properties || {}
      };
    } catch (error) {
      console.error('Error getting metadata:', error);
      throw error;
    }
  }

  // Check and enforce rate limits
  async checkRateLimit() {
    const now = Date.now();
    
    // Reset counter if time has passed
    if (now > this.rateLimiter.resetTime) {
      this.rateLimiter.requests = 0;
      this.rateLimiter.resetTime = now + 60000;
    }

    // Check if we've exceeded the limit
    if (this.rateLimiter.requests >= this.rateLimiter.maxRequests) {
      const waitTime = this.rateLimiter.resetTime - now;
      console.warn(`Rate limit exceeded. Waiting ${waitTime}ms before next request.`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
      this.rateLimiter.requests = 0;
      this.rateLimiter.resetTime = Date.now() + 60000;
    }

    this.rateLimiter.requests++;
  }

  // Utility: Convert file to ArrayBuffer
  fileToArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // Utility: Concatenate ArrayBuffers
  concatenateArrayBuffers(buffers) {
    const totalLength = buffers.reduce((sum, buffer) => sum + buffer.byteLength, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    
    for (const buffer of buffers) {
      result.set(new Uint8Array(buffer), offset);
      offset += buffer.byteLength;
    }
    
    return result;
  }

  // Token storage (implement secure storage in production)
  storeTokens() {
    if (this.accessToken) {
      localStorage.setItem('gd_access_token', this.accessToken);
    }
    if (this.refreshToken) {
      localStorage.setItem('gd_refresh_token', this.refreshToken);
    }
  }

  clearTokens() {
    localStorage.removeItem('gd_access_token');
    localStorage.removeItem('gd_refresh_token');
  }

  // Restore tokens on page load
  restoreTokens() {
    this.accessToken = localStorage.getItem('gd_access_token');
    this.refreshToken = localStorage.getItem('gd_refresh_token');
    this.isAuthenticated = !!this.accessToken;
  }
}

// Export singleton instance
const googleDriveService = new GoogleDriveService();
export default googleDriveService;